import React from "react";
import {
  ArrowUpDown,
  CheckCircle,
  Copy,
  Download,
  FileText,
  LayoutGrid,
  List,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import CustomSelect from "../../components/CustomSelect";
import AppInput from "../../components/ui/AppInput";
import ThemeTooltip from "../../components/ThemeTooltip";
import {
  TEACHER_LIST_TOOLBAR_DOWNLOAD_MENU_CLASS,
  TEACHER_LIST_TOOLBAR_DOWNLOAD_MENU_ITEM_CLASS,
  TEACHER_LIST_TOOLBAR_DOWNLOAD_TRIGGER_CLASS,
  TEACHER_LIST_TOOLBAR_LEFT_GROUP_CLASS,
  TEACHER_LIST_TOOLBAR_REGISTER_MENU_CLASS,
  TEACHER_LIST_TOOLBAR_REGISTER_MENU_ITEM_CLASS,
  TEACHER_LIST_TOOLBAR_RIGHT_ACTIONS_CLASS,
  TEACHER_LIST_TOOLBAR_SEARCH_ICON_CLASS,
  TEACHER_LIST_TOOLBAR_SEARCH_WRAPPER_CLASS,
  TEACHER_LIST_TOOLBAR_SORT_GROUP_CLASS,
  TEACHER_LIST_TOOLBAR_VIEW_TOGGLE_WRAP_CLASS,
  getTeacherListToolbarBulkDeleteButtonClass,
  getTeacherListToolbarContainerClass,
  getTeacherListToolbarPrimaryActionButtonClass,
  getTeacherListToolbarSearchInputClass,
  getTeacherListToolbarSelectAllButtonClass,
  getTeacherListToolbarSelectAllIconClass,
  getTeacherListToolbarSortToggleClass,
  getTeacherListToolbarTabButtonClass,
  getTeacherListToolbarTabGroupClass,
  getTeacherListToolbarViewModeButtonClass,
} from "./teacherListToolbarStyles";

export default function TeacherListToolbar({
  activeTab,
  availableDepartments,
  departmentFilter,
  filteredTeachersLength,
  handleBulkDelete,
  handleCopyStats,
  handleDownloadExcel,
  handleSelectAll,
  isDownloadMenuOpen,
  isMobile,
  isRegisterMenuOpen,
  isTeacher,
  openAddMinuteModal,
  openAddModal,
  registerMenuRef,
  searchTerm,
  selectedDepartment,
  selectedIds,
  setActiveTab,
  setDepartmentFilter,
  setIsBulkModalOpen,
  setIsDownloadMenuOpen,
  setIsRegisterMenuOpen,
  setSearchTerm,
  setSortKey,
  setSortOrder,
  setViewMode,
  sortKey,
  sortOrder,
  theme,
  viewMode,
}) {
  return (
    <div className={getTeacherListToolbarContainerClass(isMobile)}>
      <div className={TEACHER_LIST_TOOLBAR_LEFT_GROUP_CLASS}>
        <div className={getTeacherListToolbarTabGroupClass(isMobile)}>
          <button onClick={() => setActiveTab("list")} className={getTeacherListToolbarTabButtonClass(activeTab === "list", theme.bg, isMobile)}>명단</button>
          <button onClick={() => setActiveTab("minutes")} className={getTeacherListToolbarTabButtonClass(activeTab === "minutes", theme.bg, isMobile)}>회의록</button>
        </div>

        <div className={TEACHER_LIST_TOOLBAR_SEARCH_WRAPPER_CLASS}>
          <AppInput 
            placeholder="이름, 부서, 담당, 직분 검색" 
            className={getTeacherListToolbarSearchInputClass(theme)} 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            onClear={() => setSearchTerm("")}
            icon={Search}
          />
        </div>

        {(selectedDepartment === "All" || selectedDepartment === "전체") && (
          <CustomSelect
            className="w-[140px] flex-shrink-0"
            value={departmentFilter}
            onChange={setDepartmentFilter}
            options={[
              { value: "전체", label: "전체 부서" },
              ...availableDepartments.map((d) => ({ value: d, label: d })),
              { value: "기타", label: "기타" },
            ]}
            placeholder="부서 선택"
            theme={theme}
          />
        )}

        <div className={TEACHER_LIST_TOOLBAR_SORT_GROUP_CLASS}>
          <CustomSelect
            theme={theme}
            className="w-[120px] flex-shrink-0"
            value={sortKey}
            onChange={setSortKey}
            options={[
              { value: "name", label: "이름순" },
              { value: "role", label: "직분순" },
              { value: "responsibility", label: "담당순" },
            ]}
            showSearch={false}
            placeholder="정렬"
          />
          <button
            onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
            className={getTeacherListToolbarSortToggleClass(sortOrder, theme.text)}
            title={sortOrder === "asc" ? "오름차순" : "내림차순"}
          >
            <ArrowUpDown size={18} className={sortOrder === "desc" ? "rotate-180" : ""} />
          </button>
        </div>

        <div className={TEACHER_LIST_TOOLBAR_VIEW_TOGGLE_WRAP_CLASS}>
          <ThemeTooltip theme={theme} text="전체 선택">
            <button
              onClick={() => handleSelectAll({ target: { checked: selectedIds.length !== filteredTeachersLength } })}
              className={getTeacherListToolbarSelectAllButtonClass(selectedIds.length)}
            >
              <CheckCircle size={18} className={getTeacherListToolbarSelectAllIconClass(selectedIds.length)} />
              {selectedIds.length > 0 && <span className="text-[10px] font-bold">{selectedIds.length}</span>}
            </button>
          </ThemeTooltip>
          <div className="w-px bg-gray-200 mx-1 h-6"></div>
          <ThemeTooltip theme={theme} text="카드형 보기">
            <button onClick={() => setViewMode("card")} className={getTeacherListToolbarViewModeButtonClass(viewMode === "card", theme)}><LayoutGrid size={18} /></button>
          </ThemeTooltip>
          <ThemeTooltip theme={theme} text="리스트형 보기">
            <button onClick={() => setViewMode("list")} className={getTeacherListToolbarViewModeButtonClass(viewMode === "list", theme)}><List size={18} /></button>
          </ThemeTooltip>
        </div>
      </div>

      <div className={TEACHER_LIST_TOOLBAR_RIGHT_ACTIONS_CLASS}>
        {activeTab === "list" && !isTeacher && (
          <>
            <button onClick={handleBulkDelete} disabled={selectedIds.length === 0} className={getTeacherListToolbarBulkDeleteButtonClass(selectedIds.length)}><Trash2 size={20} /></button>

            <div className="relative">
              <button onClick={() => setIsDownloadMenuOpen(!isDownloadMenuOpen)} className={TEACHER_LIST_TOOLBAR_DOWNLOAD_TRIGGER_CLASS} title="다운로드 및 공유"><Download size={20} /></button>
              {isDownloadMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsDownloadMenuOpen(false)}></div>
                  <div className={TEACHER_LIST_TOOLBAR_DOWNLOAD_MENU_CLASS}>
                    <button onClick={() => { handleDownloadExcel(); setIsDownloadMenuOpen(false); }} className={TEACHER_LIST_TOOLBAR_DOWNLOAD_MENU_ITEM_CLASS}>
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600"><FileText size={16} /></div>
                      엑셀로 저장
                    </button>
                    <button onClick={() => { handleCopyStats(); setIsDownloadMenuOpen(false); }} className={TEACHER_LIST_TOOLBAR_DOWNLOAD_MENU_ITEM_CLASS}>
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600"><Copy size={16} /></div>
                      현황 복사
                    </button>
                  </div>
                </>
              )}
            </div>

            {!isTeacher && (
              <div className="flex gap-2">
                <div className="relative" ref={registerMenuRef}>
                  <button onClick={() => setIsRegisterMenuOpen(!isRegisterMenuOpen)} className={getTeacherListToolbarPrimaryActionButtonClass(theme.bg)}>
                    <Plus size={20} />
                    <span className="hidden sm:inline">등록</span>
                  </button>
                  {isRegisterMenuOpen && (
                    <div className={TEACHER_LIST_TOOLBAR_REGISTER_MENU_CLASS}>
                      <button onClick={() => { setIsRegisterMenuOpen(false); openAddModal(); }} className={TEACHER_LIST_TOOLBAR_REGISTER_MENU_ITEM_CLASS}><Users size={18} className={theme.text} /> 개별 등록</button>
                      <button onClick={() => { setIsRegisterMenuOpen(false); setIsBulkModalOpen(true); }} className={`${TEACHER_LIST_TOOLBAR_REGISTER_MENU_ITEM_CLASS} border-t border-gray-50`}><FileText size={18} className={theme.text} /> 일괄 등록</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        {activeTab === "minutes" && (
          <button onClick={openAddMinuteModal} className={getTeacherListToolbarPrimaryActionButtonClass(theme.bg)}><Plus size={20} /> <span className="hidden sm:inline">회의록 작성</span></button>
        )}
      </div>
    </div>
  );
}
